import { useState, useEffect, useContext } from 'react';
import { actions, StateContext } from '../../../state';
import { HTTP_REQUEST_TYPES, TOKEN_TYPES } from '../../../utils/enums';
import { HttpRequestException } from '../../../utils/errors';
import settings from '../../../conf/settings';
import { logHttpError } from '../../../utils/logging';
// import { getI18nTranslationOrDefaultValue } from "../../../utils/i18n";

export const FinixFlow = ({ recipientLogin, children }) => {
  const [applicationState, dispatch] = useContext(StateContext);
  const { configuration, accountConfiguration } = applicationState;
  const [cardType, setCardType] = useState(null);
  const [error, setError] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);
  const [isFetching, setisFetching] = useState(false);

  const finixOptions = {
    // show address fields in the form (default is false)
    showAddress: true,
    //show labels in the form (default is true)
    showLabels: false,
    // turn on or off placeholder text in the fields (default is true)
    showPlaceholders: true,
    // hide specific fields that you do not need
    hideFields: [
      // Supported Fields: "name", "security_code", "address_line1", "address_line2", "address_city", "address_state","address_region", "address_country", "address_postal_code", "address_country"
      // "name",
      // "address_line1",
      // "address_line2",
      // "address_city",
      'address_region',
      //"address_state",
      'address_country',
    ],
    // require any specific fields that are not required by default, you can specify them here
    requiredFields: [
      // Supported Fields: "name", "address_line1", "address_line2", "address_city", "address_state","address_region", "address_country", "address_postal_code"
      'name',
      'address_line1',
      'address_city',
      'address_state',
      'address_postal_code',
    ],
    // if you want to require a field, but not hide input error messages (default is false)
    hideErrorMessages: true,
    // custom styles for the form inputs (optional but recommended)
    styles: {
      // default styling for all fields
      default: {
        background: '#FFF',
        color: '#000',
        border: '1px solid #CCCDCF',
        borderRadius: '8px',
        padding: '8px 16px',
        fontFamily: 'Helvetica',
        fontSize: '14px',
        boxShadow:
          '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 2px 4px rgba(0, 0, 0, 0.03)',
      },
      success: {
        color: '#5cb85c',
      },
      error: {
        color: '#d9534f',
      },
    },
    // optional callback function that will trigger when form state changes (can be called frequently)
    onUpdate: function (state, binInformation, formHasErrors) {
      // console.log(state);
      // console.log(binInformation);
      // console.log(formHasErrors);
      if (state === undefined) {
        parseBinInformation(binInformation);
      } else {
        // setCanSubmit(checkAllFinixFieldsValid(state))
        setCanSubmit(!formHasErrors);
      }
    },
    // optional callback function that will trigger after the form has loaded
    onLoad: function () {
      // hide loading spinners or anything else you want to do
    },
    // optional callback function that will be called when the form is submitted
    // NOTE: adding this option will automatically create a submit button for you.
    // If you do not want to use the default button and create your own,
    // do not supply this function and instead create your own submit button
    // and attach the onSubmit function to it manually.
    //onSubmit,
  };

  let finixFormInstance = null;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    finixFormInstance = window.Finix.CardTokenForm('cc-form', finixOptions);

    document.getElementById('cc-form').addEventListener(
      'submit',
      (e) => {
        e.preventDefault();
        submitFinixForm();
      },
      false
    );
  }, []);

  const submitFinixForm = () => {
    setisFetching(true);

    finixFormInstance.submit(
      settings.environment.DEPLOYMENT_ENVIRONMENT === 'DEV'
        ? 'sandbox'
        : 'production',
      accountConfiguration.applicationId,
      (err, res) => {
        if (err) {
          onTokenizationError(res, err);
        } else {
          onTokenizationSuccess(res);
        }
      }
    );
  };

  const parseBinInformation = (binInformation) => {
    const cc_type = binInformation.cardBrand;
    setCardType(binInformation.cardBrand);

    checkCardType(cc_type);
  };

  const checkCardType = (cc_type) => {
    const { acceptedCardTypes } = configuration.recipientLogin.props;
    // display error message if user is entering a non-visa card
    if (cc_type && acceptedCardTypes.cardTypes.indexOf(cc_type) === -1) {
      setError(acceptedCardTypes.errorMessage);
    } else {
      setError(null);
    }
  };

  const onTokenizationSuccess = async (finixResponse) => {
    const finixToken = finixResponse.data.id;
    const dataForRecipientLogin = { token: finixToken };

    try {
      const recipientLoginResponse = await recipientLogin({
        dataForRecipientLogin,
        skipDefaultPostEffects: true,
      });
      setisFetching(false);
      dispatch(
        actions.tokens.setToken(
          TOKEN_TYPES.RECIPIENT_LOGIN,
          recipientLoginResponse.data
        )
      );
    } catch (error) {
      setisFetching(false);

      const httpRequestException = HttpRequestException(
        HTTP_REQUEST_TYPES.RECIPIENT_LOGIN,
        error
      );
      logHttpError(httpRequestException, applicationState);
      dispatch(
        actions.modals.showHttpErrorModal(configuration, {
          errorType: httpRequestException.errorType,
          serverResponse: httpRequestException.serverResponse,
          propsByHttpStatus: {
            default: {
              allowClose: true,
              showChooseAnotherNetwork: true,
              actions: [
                {
                  label: 'Use Another Card',
                  onClick: () => {},
                },
              ],
            },
            401: {
              showChooseAnotherNetwork: false,
              showFaqLink: false,
              allowClose: false,
              actions: [
                {
                  label: 'Start Over',
                  onClick: () => dispatch(actions.startOver()),
                },
              ],
            },
          },
        })
      );
    }
  };

  const onTokenizationError = (response) => {
    const { data } = response;

    console.error(`Finix tokenization failed: ${data}`);

    const errors = data._embedded.errors;
    let errorMessage =
      'An unknown error occurred. Please try your request again.';
    if (errors && errors.length) {
      const error = errors[0];

      switch (error.code) {
        case 'INVALID_FIELD':
          errorMessage = `Problem with ${error.field.replace('_', ' ')} (${error.message})`;
          break;
        default:
          errorMessage = `Tokenization Error: message: ${error.message}, code: ${error.code}, field: ${error.field}, logref: ${error.logref}`;
        // TODO log this error to loggly
      }
    }

    setisFetching(false);
    setError(errorMessage);
  };

  // const checkAllFinixFieldsValid = formState => Object.keys(formState).reduce((allValid, fieldName) => (allValid && formState[fieldName].isDirty && !formState[fieldName].errorMessages.length), true);

  return children({
    cardType,
    canSubmit: canSubmit && !error,
    onSubmit: () => {
      const event = new Event('submit');
      document.getElementById('cc-form').dispatchEvent(event);
    },
    isFetching,
    error,
  });
};

export default FinixFlow;
